// forms/nearMissForm/actions.js

import { makePhotosActions } from "../photosForm/photosFormActions";
import { makeNearMissAutosaveErrorsActions } from "./autosaveErrors/nearMissFormAutosaveErrorsActions";

const bump = (set, get, tabKey, patch = null) => {
  const s = get();
  const prev = s[tabKey] || {};
  const nextRev = (prev.rev || 0) + 1;
  const next = patch
    ? { [tabKey]: { ...prev, rev: nextRev, ...patch } }
    : { [tabKey]: { ...prev, rev: nextRev } };
  set(next);
};

export const makeNearMissFormActionsForTab = (set, get, tabKey) => {
  const api = {
    // Helper to get the current near miss from the tab
    getCurrentNearMiss: () => {
      const slice = get()[tabKey];
      if (!slice || !slice.currentNearMiss) {
        throw new Error(
          `No currentNearMiss found in ${tabKey}. Did you call startNewNearMiss() or fetchNearMiss()?`
        );
      }
      return slice.currentNearMiss;
    },

    // Bump revision for autosave tracking
    bump: (patch) => bump(set, get, tabKey, patch),

    // UI update helper for signature modal
    setNearMissFormUI: (patch) => {
      const s = get();
      const prev = s[tabKey].nearMissForm.nearMissFormUI;
      const nextPatch = typeof patch === "function" ? patch(prev) : patch;
      set({
        [tabKey]: {
          ...s[tabKey],
          nearMissForm: {
            ...s[tabKey].nearMissForm,
            nearMissFormUI: { ...prev, ...nextPatch },
          },
        },
      });
    },

    // UI update helper for photos
    setPhotosUi: (patch) => {
      const s = get();
      const prev = s[tabKey].nearMissForm.photosUI;
      const nextPatch = typeof patch === "function" ? patch(prev) : patch;
      set({
        [tabKey]: {
          ...s[tabKey],
          nearMissForm: {
            ...s[tabKey].nearMissForm,
            photosUI: { ...prev, ...nextPatch },
          },
        },
      });
    },

    // Get photos UI state
    getPhotosUi: () => {
      return get()[tabKey].nearMissForm.photosUI;
    },
  };

  return Object.freeze({
    // Basic incident information
    updateIncidentDate(incidentDate, emitPatch = null) {
      const nearMiss = api.getCurrentNearMiss();
      nearMiss.updateIncidentDate(incidentDate);
      api.bump();
      if (emitPatch) {
        const patch = nearMiss.toPatch({ incidentDate: nearMiss.incident_date });
        emitPatch(patch);
      }
    },

    updateIncidentTime(incidentTime, emitPatch = null) {
      const nearMiss = api.getCurrentNearMiss();
      nearMiss.updateIncidentTime(incidentTime);
      api.bump();
      if (emitPatch) {
        const patch = nearMiss.toPatch({ incidentTime });
        emitPatch(patch);
      }
    },

    updateLocation(location, emitPatch = null) {
      const nearMiss = api.getCurrentNearMiss();
      nearMiss.updateLocation(location);
      api.bump();
      if (emitPatch) {
        const patch = nearMiss.toPatch({ location });
        emitPatch(patch);
      }
    },

    updateSite(site, emitPatch = null) {
      const nearMiss = api.getCurrentNearMiss();
      nearMiss.updateSite(site);
      api.bump();
      if (emitPatch) {
        const patch = nearMiss.toPatch({ site });
        emitPatch(patch);
      }
    },

    // Reporting information
    updateReportedTo(reportedTo, emitPatch = null) {
      const nearMiss = api.getCurrentNearMiss();
      if (!reportedTo) {
        nearMiss.updateReportedTo(null);
        nearMiss.updateReportedToName(null);
        api.bump();
        if (emitPatch) {
          const patch = nearMiss.toPatch({ reportedTo: null });
          emitPatch(patch);
        }
        return;
      }
      nearMiss.updateReportedTo(reportedTo.value);
      nearMiss.updateReportedToName(reportedTo.label);
      api.bump();
      if (emitPatch) {
        const patch = nearMiss.toPatch({ reportedTo: reportedTo.value });
        emitPatch(patch);
      }
    },

    updateReportedBy(reportedBy, emitPatch = null) {
      const nearMiss = api.getCurrentNearMiss();
      if (!reportedBy) {
        nearMiss.updateReportedBy(null);
        nearMiss.updateReportedByName(null);
        nearMiss.updateJobTitle(null);
        api.bump();
        if (emitPatch) {
          const patch = nearMiss.toPatch({ reportedBy: null, jobTitle: null });
          emitPatch(patch);
        }
        return;
      }
      nearMiss.updateReportedBy(reportedBy.value);
      nearMiss.updateReportedByName(reportedBy.label);
      nearMiss.updateJobTitle(reportedBy.job_title);
      api.bump();
      if (emitPatch) {
        const patch = nearMiss.toPatch({
          reportedBy: reportedBy.value,
          jobTitle: reportedBy.job_title
        });
        emitPatch(patch);
      }
    },

    updateJobTitle(jobTitle, emitPatch = null) {
      const nearMiss = api.getCurrentNearMiss();
      nearMiss.updateJobTitle(jobTitle);
      api.bump();
      if (emitPatch) {
        const patch = nearMiss.toPatch({ jobTitle });
        emitPatch(patch);
      }
    },

    // Description
    updateNearMissDescription(nearMissDescription, emitPatch = null) {
      const nearMiss = api.getCurrentNearMiss();
      nearMiss.updateNearMissDescription(nearMissDescription);
      api.bump();
      if (emitPatch) {
        const patch = nearMiss.toPatch({ nearMissDescription });
        emitPatch(patch);
      }
    },

    // Potential hazards
    updatePotHazSTF(potHazSTF, emitPatch = null) {
      const nearMiss = api.getCurrentNearMiss();
      nearMiss.updatePotHazSTF(potHazSTF);
      api.bump();
      if (emitPatch) {
        const patch = nearMiss.toPatch({ potHazStf: potHazSTF });
        emitPatch(patch);
      }
    },

    updatePotHazFO(potHazFO, emitPatch = null) {
      const nearMiss = api.getCurrentNearMiss();
      nearMiss.updatePotHazFO(potHazFO);
      api.bump();
      if (emitPatch) {
        const patch = nearMiss.toPatch({ potHazFo: potHazFO });
        emitPatch(patch);
      }
    },

    updatePotHazCE(potHazCE, emitPatch = null) {
      const nearMiss = api.getCurrentNearMiss();
      nearMiss.updatePotHazCE(potHazCE);
      api.bump();
      if (emitPatch) {
        const patch = nearMiss.toPatch({ potHazCe: potHazCE });
        emitPatch(patch);
      }
    },

    updatePotHazEH(potHazEH, emitPatch = null) {
      const nearMiss = api.getCurrentNearMiss();
      nearMiss.updatePotHazEH(potHazEH);
      api.bump();
      if (emitPatch) {
        const patch = nearMiss.toPatch({ potHazEh: potHazEH });
        emitPatch(patch);
      }
    },

    updatePotHazEF(potHazEF, emitPatch = null) {
      const nearMiss = api.getCurrentNearMiss();
      nearMiss.updatePotHazEF(potHazEF);
      api.bump();
      if (emitPatch) {
        const patch = nearMiss.toPatch({ potHazEf: potHazEF });
        emitPatch(patch);
      }
    },

    updatePotHazIPPE(potHazIPPE, emitPatch = null) {
      const nearMiss = api.getCurrentNearMiss();
      nearMiss.updatePotHazIPPE(potHazIPPE);
      api.bump();
      if (emitPatch) {
        const patch = nearMiss.toPatch({ potHazIppe: potHazIPPE });
        emitPatch(patch);
      }
    },

    updatePotHazMHI(potHazMHI, emitPatch = null) {
      const nearMiss = api.getCurrentNearMiss();
      nearMiss.updatePotHazMHI(potHazMHI);
      api.bump();
      if (emitPatch) {
        const patch = nearMiss.toPatch({ potHazMhi: potHazMHI });
        emitPatch(patch);
      }
    },

    updatePotHazFEH(potHazFEH, emitPatch = null) {
      const nearMiss = api.getCurrentNearMiss();
      nearMiss.updatePotHazFEH(potHazFEH);
      api.bump();
      if (emitPatch) {
        const patch = nearMiss.toPatch({ potHazFeh: potHazFEH });
        emitPatch(patch);
      }
    },

    updatePotHazCSI(potHazCSI, emitPatch = null) {
      const nearMiss = api.getCurrentNearMiss();
      nearMiss.updatePotHazCSI(potHazCSI);
      api.bump();
      if (emitPatch) {
        const patch = nearMiss.toPatch({ potHazCsi: potHazCSI });
        emitPatch(patch);
      }
    },

    updatePotHazOther(potHazOther, emitPatch = null) {
      const nearMiss = api.getCurrentNearMiss();
      nearMiss.updatePotHazOther(potHazOther);
      api.bump();
      if (emitPatch) {
        const patch = nearMiss.toPatch({ potHazOther });
        emitPatch(patch);
      }
    },

    // Contributing factors and actions
    updateContributingFactors(contributingFactors, emitPatch = null) {
      const nearMiss = api.getCurrentNearMiss();
      nearMiss.updateContributingFactors(contributingFactors);
      api.bump();
      if (emitPatch) {
        const patch = nearMiss.toPatch({ contributingFactors });
        emitPatch(patch);
      }
    },

    updateActionsTaken(actionsTaken, emitPatch = null) {
      const nearMiss = api.getCurrentNearMiss();
      nearMiss.updateActionsTaken(actionsTaken);
      api.bump();
      if (emitPatch) {
        const patch = nearMiss.toPatch({ actionsTaken });
        emitPatch(patch);
      }
    },

    updateSagCorrectiveActions(sagCorrectiveActions, emitPatch = null) {
      const nearMiss = api.getCurrentNearMiss();
      nearMiss.updateSagCorrectiveActions(sagCorrectiveActions);
      api.bump();
      if (emitPatch) {
        const patch = nearMiss.toPatch({ sagCorrectiveActions });
        emitPatch(patch);
      }
    },

    // Immediate assessment
    updateImmediateAssYes(immediateAssYes, emitPatch = null) {
      const nearMiss = api.getCurrentNearMiss();
      nearMiss.updateImmediateAssYes(immediateAssYes);
      api.bump();
      if (emitPatch) {
        // When setting Yes, also send No = false
        const patch = nearMiss.toPatch({
          immediateAssYes,
          immediateAssNo: false
        });
        emitPatch(patch);
      }
    },

    updateImmediateAssNo(immediateAssNo, emitPatch = null) {
      const nearMiss = api.getCurrentNearMiss();
      nearMiss.updateImmediateAssNo(immediateAssNo);
      nearMiss.updateImmediateAssYesDescription(null);
      api.bump();
      if (emitPatch) {
        // When setting No, also send Yes = false and clear description
        const patch = nearMiss.toPatch({
          immediateAssNo,
          immediateAssYes: false,
          immediateAssYesDescription: null
        });
        emitPatch(patch);
      }
    },

    updateImmediateAssYesDescription(immediateAssYesDescription, emitPatch = null) {
      const nearMiss = api.getCurrentNearMiss();
      nearMiss.updateImmediateAssYesDescription(immediateAssYesDescription);
      api.bump();
      if (emitPatch) {
        const patch = nearMiss.toPatch({ immediateAssYesDescription });
        emitPatch(patch);
      }
    },

    // Signature
    updateSignature(signature, employeeId, emitPatch = null) {
      const nearMiss = api.getCurrentNearMiss();
      nearMiss.updateSignature(signature);
      nearMiss.updateSignatureEmployeeId(employeeId);
      api.bump();
      if (emitPatch) {
        // Strip base64 prefix for WebSocket transmission
        const signatureData = signature
          ? signature.replace("data:image/png;base64,", "").trim()
          : null;
        const patch = nearMiss.toPatch({
          signature: signatureData,
          signatureEmployeeId: employeeId
        });
        emitPatch(patch);
      }
    },

    updateSignatureDate(signatureDate, emitPatch = null) {
      const nearMiss = api.getCurrentNearMiss();
      nearMiss.updateSignatureDate(signatureDate);
      api.bump();
      if (emitPatch) {
        const patch = nearMiss.toPatch({ signatureDate: nearMiss.signature_date });
        emitPatch(patch);
      }
    },

    // Signature Modal UI Actions
    toggleSignatureModal(isOpen) {
      const nearMiss = api.getCurrentNearMiss();
      api.setNearMissFormUI({
        signatureModalOpen: isOpen,
        currentSignatureImage: isOpen ? nearMiss.signature : null,
      });
    },

    closeSignatureModal() {
      api.setNearMissFormUI({
        signatureModalOpen: false,
        currentSignatureImage: null,
      });
    },

    saveSignature(signature, emitPatch = null, user = null) {
      const nearMiss = api.getCurrentNearMiss();
      nearMiss.updateSignature(signature);
      // Set the signature employee to whoever is signing
      if (user) {
        nearMiss.updateSignatureEmployeeId(user.employee_id);
        nearMiss.updateSignatureEmployeeName(user.name);
      }
      // Auto-set signature date to today if not already set
      if (!nearMiss.signature_date) {
        nearMiss.updateSignatureDate(new Date());
      }
      api.setNearMissFormUI({
        signatureModalOpen: false,
        currentSignatureImage: null,
      });
      api.bump();
      if (emitPatch) {
        // Strip base64 prefix for WebSocket transmission
        const signatureData = signature
          ? signature.replace("data:image/png;base64,", "").trim()
          : null;
        const patch = nearMiss.toPatch({
          signature: signatureData,
          signatureDate: nearMiss.signature_date,
          signatureEmployeeId: nearMiss.signature_employee_id
        });
        emitPatch(patch);
      }
    },

    clearSignature(emitPatch = null) {
      const nearMiss = api.getCurrentNearMiss();
      nearMiss.updateSignature("");
      nearMiss.updateSignatureDate(null);
      nearMiss.updateSignatureEmployeeId(null);
      nearMiss.updateSignatureEmployeeName(null);
      api.bump();
      if (emitPatch) {
        const patch = nearMiss.toPatch({
          signature: null,
          signatureDate: null,
          signatureEmployeeId: null
        });
        emitPatch(patch);
      }
    },

    // UI helpers
    setNearMissFormUI: api.setNearMissFormUI,
    getCurrentNearMiss: api.getCurrentNearMiss,
    bump: api.bump,

    // Tab navigation action
    setActiveTab(tab) {
      api.setNearMissFormUI({ activeTab: tab });
    },

    // Photos actions using factory
    photos: makePhotosActions({
      getFormData: () => api.getCurrentNearMiss(),
      setPhotosUi: api.setPhotosUi,
      getPhotosUi: api.getPhotosUi,
      bump: () => api.bump(),
    }),

    // Autosave errors actions
    autosaveErrors: makeNearMissAutosaveErrorsActions(set, get, tabKey),
  });
};
